import { GAME } from '@/config/game';
import { MAPS } from '@/config/maps';
import {
  dirVec, isHorizontal, oppositeDir, type Dir, type Door, type Piece, type Rect,
} from './geometry';
import type { ObstacleDef } from './layout-types';

/** One fight segment of an ADVANCE route (docs/01 advance rooms). */
export interface RouteSegmentDef {
  /** Exit side = travel direction through the segment */
  readonly exit: Dir;
  /** Outer size along the exit direction / across it (px, incl. walls) */
  readonly len: number;
  readonly span: number;
  /** Door positions on the entry / exit side (0..1 along that side, default 0.5) */
  readonly entryAt?: number;
  readonly exitAt?: number;
  /** Cover/hazards — fractions of the segment's outer rect (x → w, y → h) */
  readonly obstacles?: ReadonlyArray<ObstacleDef>;
  /** Gate guards spawned when the segment's fight starts (fractions) */
  readonly turrets?: ReadonlyArray<{ x: number; y: number }>;
}

/** The boss chamber closing a route — always landscape, sized per boss. */
export interface RouteChamberDef {
  /** Entry door position on the entry side (0..1, default 0.5) */
  readonly entryAt?: number;
  readonly obstacles?: ReadonlyArray<ObstacleDef>;
}

export interface RouteDef {
  readonly segments: ReadonlyArray<RouteSegmentDef>;
  readonly chamber: RouteChamberDef;
}

/** A laid-out route: pieces (segments…, chamber last) + the doors between. */
export interface BuiltRoute {
  readonly pieces: ReadonlyArray<Piece>;
  readonly doors: Door[];
  /** Obstacles per piece (same index), fractions of the piece's outer rect */
  readonly obstacles: ReadonlyArray<ReadonlyArray<ObstacleDef>>;
  readonly turrets: ReadonlyArray<ReadonlyArray<{ x: number; y: number }>>;
  /** Bounding box of every outer rect (world px, may start below 0) */
  readonly bounds: Rect;
}

/** Outer rect of a piece whose entry side touches `p` at fraction `at`. */
function placePiece(entrySide: Dir, p: { x: number; y: number }, at: number, w: number, h: number): Rect {
  switch (entrySide) {
    case 'down': return { x: p.x - at * w, y: p.y - h, w, h };
    case 'up': return { x: p.x - at * w, y: p.y, w, h };
    case 'left': return { x: p.x, y: p.y - at * h, w, h };
    default: return { x: p.x - w, y: p.y - at * h, w, h };
  }
}

/** Door point on a rect's side `side` at fraction `at`. */
function sidePoint(r: Rect, side: Dir, at: number): { x: number; y: number } {
  switch (side) {
    case 'up': return { x: r.x + at * r.w, y: r.y };
    case 'down': return { x: r.x + at * r.w, y: r.y + r.h };
    case 'left': return { x: r.x, y: r.y + at * r.h };
    default: return { x: r.x + r.w, y: r.y + at * r.h };
  }
}

/** Axis-aligned box from two corners. */
function box(ax: number, ay: number, bx: number, by: number): Rect {
  return { x: Math.min(ax, bx), y: Math.min(ay, by), w: Math.abs(bx - ax), h: Math.abs(by - ay) };
}

/** Doorway + gate across the bulkhead starting at `exit` (piece side) in `dir`. */
function makeDoor(from: number, exit: { x: number; y: number }, dir: Dir): Door {
  const R = MAPS.route;
  const wall = GAME.arena.wallThickness;
  const v = dirVec(dir);
  const half = R.doorWidth / 2;
  const reach = wall + R.doorJoin;
  const a = { x: exit.x - v.x * reach, y: exit.y - v.y * reach };
  const b = { x: exit.x + v.x * (R.bulkhead + reach), y: exit.y + v.y * (R.bulkhead + reach) };
  const mid = { x: exit.x + v.x * R.bulkhead / 2, y: exit.y + v.y * R.bulkhead / 2 };
  const gh = R.gateThickness / 2;
  const gw = half + R.gateOverhang;
  const horiz = isHorizontal(dir);
  const rect = horiz ? box(a.x, exit.y - half, b.x, exit.y + half) : box(exit.x - half, a.y, exit.x + half, b.y);
  const gate = horiz
    ? box(mid.x - gh, mid.y - gw, mid.x + gh, mid.y + gw)
    : box(mid.x - gw, mid.y - gh, mid.x + gw, mid.y + gh);
  return { from, to: from + 1, rect, gate, dir, closed: true, fadeT: 0 };
}

/**
 * Lay a route out piece by piece (each entry door lines up with the previous
 * exit door across a bulkhead), then shift everything so the boss chamber's
 * outer rect starts at the origin — boss code assumes (0,0)..(arenaW,arenaH).
 */
export function buildRoute(def: RouteDef, chamberW: number, chamberH: number): BuiltRoute {
  const wall = GAME.arena.wallThickness;
  const outers: Rect[] = [];
  const dirs: Dir[] = [];
  const exits: Array<{ x: number; y: number }> = [];
  let entry = { x: 0, y: 0 };
  let entrySide: Dir = oppositeDir(def.segments[0]?.exit ?? 'right');

  def.segments.forEach((s, i) => {
    const w = isHorizontal(s.exit) ? s.len : s.span;
    const h = isHorizontal(s.exit) ? s.span : s.len;
    const at = i === 0 ? 0.5 : s.entryAt ?? 0.5;
    const outer = i === 0 ? { x: -w / 2, y: -h / 2, w, h } : placePiece(entrySide, entry, at, w, h);
    const exit = sidePoint(outer, s.exit, s.exitAt ?? 0.5);
    outers.push(outer);
    dirs.push(s.exit);
    exits.push(exit);
    const v = dirVec(s.exit);
    entry = { x: exit.x + v.x * MAPS.route.bulkhead, y: exit.y + v.y * MAPS.route.bulkhead };
    entrySide = oppositeDir(s.exit);
  });
  const lastDir = dirs[dirs.length - 1] ?? 'right';
  const chamber = placePiece(entrySide, entry, def.chamber.entryAt ?? 0.5, chamberW, chamberH);
  outers.push(chamber);
  dirs.push(lastDir);

  // Shift: chamber outer at the origin
  const ox = chamber.x;
  const oy = chamber.y;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const pieces: Piece[] = outers.map((o, index) => {
    const outer = { x: o.x - ox, y: o.y - oy, w: o.w, h: o.h };
    minX = Math.min(minX, outer.x);
    minY = Math.min(minY, outer.y);
    maxX = Math.max(maxX, outer.x + outer.w);
    maxY = Math.max(maxY, outer.y + outer.h);
    const inner = { x: outer.x + wall, y: outer.y + wall, w: outer.w - wall * 2, h: outer.h - wall * 2 };
    const seg = index < outers.length - 1;
    const F = MAPS.route.segmentFrame;
    return { outer, inner, dir: dirs[index]!, index, u0: seg ? F.u0 : 0, uSpan: seg ? F.span : 1 };
  });
  const doors = exits.map((e, i) => makeDoor(i, { x: e.x - ox, y: e.y - oy }, dirs[i]!));
  return {
    pieces,
    doors,
    obstacles: [...def.segments.map((s) => s.obstacles ?? []), def.chamber.obstacles ?? []],
    turrets: [...def.segments.map((s) => s.turrets ?? []), []],
    bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
  };
}
